import type { ShowType } from './types'

/**
 * Errors produced by the browser rather than the backend. Negative values can never collide
 * with backend error codes, which are always 6-digit positive numbers.
 */
export const ClientErrorCode = {
  NetworkError: -1,
  Timeout: -2,
  Canceled: -3,
  UnexpectedResponse: -4,
  Unknown: -5,
} as const

export type ClientErrorCode = (typeof ClientErrorCode)[keyof typeof ClientErrorCode]

interface ApiErrorOptions {
  errorCode: number
  message: string
  showType: ShowType
  httpStatus?: number
  requestId?: string
  data?: unknown
  cause?: unknown
}

/** The single error type every request rejects with. Compare `errorCode`, never `message`. */
export class ApiError extends Error {
  readonly errorCode: number
  readonly showType: ShowType
  readonly httpStatus: number | undefined
  readonly requestId: string | undefined
  readonly data: unknown

  constructor(options: ApiErrorOptions) {
    super(options.message, { cause: options.cause })
    this.name = 'ApiError'
    this.errorCode = options.errorCode
    this.showType = options.showType
    this.httpStatus = options.httpStatus
    this.requestId = options.requestId
    this.data = options.data
  }

  get isClientError(): boolean {
    return this.errorCode < 0
  }

  get isCanceled(): boolean {
    return this.errorCode === ClientErrorCode.Canceled
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

export function hasErrorCode(error: unknown, errorCode: number): boolean {
  return isApiError(error) && error.errorCode === errorCode
}

/** Backend error codes the frontend reacts to explicitly. Values come from Tokki-backend errcode.go. */
export const BackendErrorCode = {
  Internal: 100000,
  InvalidParams: 100001,
  TooManyRequests: 100004,
  Unauthorized: 110001,
  Forbidden: 110002,
} as const
